import React, { useState, useCallback, useEffect, useRef } from 'react'
import moment from 'moment'
import _ from 'lodash'
import { convertToSentenceCase } from '../../services/utils'
import Widget from '../../dpe-usco/components/Widget/Widget'
import TabbedTable from '../../components/TabbedTable/TabbedTable'
import TableStatus from '../../components/subComponents/TableStatus/TableStatus'
import Select from '../../dpe-usco/components/Select/Select'
import { BEEF_MULTIPLIER, DAIRY_MULTIPLIER } from '../../services/config'

const COMMERCIAL_FEED = 'commercial-feed'
const FED_ON_FARM = 'fed-on-farm'
export const generateDeliveryMonths = year => {
  const startDate = year ? `${year}-09-01` : new Date().getFullYear() + '-09-01'
  const momentDate = moment(startDate)
  const _monthArray = []
  const _monthObj = {}
  for (let i = 0;i < 13;i++) {
    const newDate = momentDate.clone().add(i, 'month')
    const delivery_month = newDate.format('MMM-YY')
    const month = newDate.format('MMMM').toLowerCase()
    const year = newDate.format('YYYY')
    _monthArray.push(
      {
        delivery_month,
        available_acres: 0,
        available_units: 0,
        month,
        year
      }
    )
    _monthObj[delivery_month] = {
      month,
      year,
      available_acres: 0,
      available_units: 0,
      contract_acres: 0,
      contract_units: 0
    }
  }
  return [_monthObj, _monthArray]
}

const generateMonthObject = (year, deliveryType) => {
  const startDate = year ? `${year}-09-01` : new Date().getFullYear() + '-09-01'
  const momentDate = moment(startDate)
  const monthObj = {}
  for (let i = 0;i < 13;i++) {
    const newDate = momentDate.clone().add(i, 'month')
    const delivery_month = newDate.format('MMM-YY')
    const month = newDate.format('MMMM').toLowerCase()
    const year = newDate.format('YYYY')
    monthObj[delivery_month] = {
      delivery_month,
      month,
      year,
      contract_acres: 0,
      contract_units: 0,
      delivery_type: deliveryType
    }
  }
  return monthObj
}

const emptyArray = []
const [_monthObj, _monthArray] = generateDeliveryMonths()

const DeliveryScheduleWidget = ({ contractYear, initialMonthlyUnits, fieldData, returnData, contractType, newMonthlyUnits, updatedMonthlyUnits, _defaultDeliverySchedule, _defaultCattleInfo, totalAPH, defaultAphEvent, satusResetDeliveryTable, setSatusResetDeliveryTable }) => {
  const [remaining, setRemaining] = useState({ remaining_acres: 0, remaining_units: 0 })

  const [silageInputs, setSilageInputs] = useState({ ..._monthObj })
  const [earlageInputs, setEarlageInputs] = useState({ ..._monthObj })
  const [highMoistureCornInputs, setHighMoistureCornInputs] = useState({ ..._monthObj })
  const [grainInputs, setGrainInputs] = useState({ ..._monthObj })
  const [silageMonths, setSilageMonths] = useState([..._monthArray])
  const [earlageMonths, setEarlageMonths] = useState([..._monthArray])
  const [highMoistureCornMonths, setHighMoistureCornMonths] = useState([..._monthArray])
  const [grainMonths, setGrainMonths] = useState([..._monthArray])
  const [totalAcres, setTotalAcres] = useState(0)
  const [totalUnits, setTotalUnits] = useState(0)
  const [selectedCattleType, setSelectedCattleType] = useState()
  const [bushels, setBushels] = useState(0)
  const [headCount, setHeadCount] = useState(0)
  const [checkboxStatus, setCheckboxStatus] = useState(0)

  useEffect(() => {
    if(satusResetDeliveryTable) {
      const [_monthObjReset] = generateDeliveryMonths(contractYear)
      setCheckboxStatus(true)
      setSilageInputs({ ..._monthObjReset })
      setEarlageInputs({ ..._monthObjReset })
      setHighMoistureCornInputs({ ..._monthObjReset })
      setGrainInputs({ ..._monthObjReset })
      const delivery_schedule_error = {
        empty: {
          state: false,
          value:
              'Please completely fill out at least one month inside the delivery schedule'
        },
        cattle_empty: {
          state: false,
          value: 'Please provide a cattle type and count'
        }
      }
      updatedMonthlyUnits(false)
      returnData({ type: 'UPDATE_DELIVERY_SCHEDULE', payload: [] })
      returnData({ type: 'CLEAR_ERROR', payload: { delivery_schedule: delivery_schedule_error } })
      setCheckboxStatus(false)
      setTotalAcres(Math.round(0))
      setTotalUnits(Math.round(0))
      setSatusResetDeliveryTable(false)
    }
  },[satusResetDeliveryTable, setSatusResetDeliveryTable, contractYear,returnData,updatedMonthlyUnits,setTotalAcres,setTotalUnits])


  const getProductTypeState = useCallback(type => {
    const silage = {
      type: 'silage',
      state: silageInputs,
      setter: setSilageInputs,
      months: silageMonths,
      monthsSetter: setSilageMonths
    }

    const earlage = {
      type: 'earlage',
      state: earlageInputs,
      setter: setEarlageInputs,
      months: earlageMonths,
      monthsSetter: setEarlageMonths
    }

    const highMoistureCorn = {
      type: 'high-moisture',
      state: highMoistureCornInputs,
      setter: setHighMoistureCornInputs,
      months: highMoistureCornMonths,
      monthsSetter: setHighMoistureCornMonths
    }
    const grain = {
      type: 'grain',
      state: grainInputs,
      setter: setGrainInputs,
      months: grainMonths,
      monthsSetter: setGrainMonths
    }

    if (contractType === COMMERCIAL_FEED) {
      switch (type) {
        case 'SILAGE':
          return silage
        case 'EARLAGE':
          return earlage
        case 'HIGH MOISTURE CORN':
          return highMoistureCorn
        case 'GRAIN':
          return grain
        case 'ALL':
          return [
            grain, silage, earlage, highMoistureCorn
          ]
        default:
          return null
      }
    } else {
      switch (type) {
        case 'GRAIN':
          return grain
        case 'ALL':
          return [grain]
        default:
          return null
      }
    }
  }, [
    silageInputs, silageMonths, earlageInputs, earlageMonths, highMoistureCornInputs, highMoistureCornMonths, grainInputs, grainMonths, contractType
  ])

  const updateDeliveryMonth = (e, month, attribute, tab) => {
    const temp = { ...e }
    const setterFunction = getProductTypeState(tab).setter
    let availData
    let calculatedData
    if(attribute === 'contract_acres'){
      availData = 'contract_units'
      calculatedData = Math.round(temp.target.value*totalAPH)
    }
    if(attribute === 'contract_units'){
      availData = 'contract_acres'
      if(temp.target.value === 0 || temp.target.value === ''){
        calculatedData = '0'
      }else{
        let result=Math.round(temp.target.value/totalAPH)
        if(isFinite(result)){
          calculatedData = result.toString()
        }else{
          calculatedData = '0'
        } 
      }
    }
    setterFunction(prevDeliveryMonths => {
      return {
        ...prevDeliveryMonths,
        [month]: {
          ...prevDeliveryMonths[month],
          [attribute]: temp.target.value,
          [availData]:calculatedData.toString()
        }
      }
    })
  }

  useEffect(()=>{
    if(defaultAphEvent) {
      setGrainInputs(prevDeliveryMonths => {
        Object.keys(prevDeliveryMonths).forEach(function(key){
          let unitsValue=prevDeliveryMonths[key]['contract_units']
          if(unitsValue === 0 || unitsValue === ''){
            prevDeliveryMonths[key]['contract_units'] = '0'
          }else{
            let result=Math.round(unitsValue/totalAPH)
            if(isFinite(result)){
              prevDeliveryMonths[key]['contract_acres'] = result.toString()
            }else{
              prevDeliveryMonths[key]['contract_acres'] = '0'
            }
            
          }    
        })
        return { ...prevDeliveryMonths }    
      })  
    }
  },[totalAPH, defaultAphEvent])
  
  const handleSelectYear = useCallback(option => {
    const allInputs = getProductTypeState('ALL')
    // This shouldn't delete all the user data
    allInputs.forEach(({ setter, monthsSetter }) => {
      const [_monthObj, _monthArray] = generateDeliveryMonths(option)
      setter(prev => {
        return { ..._monthObj }
      })
      monthsSetter([..._monthArray])
    })
  }, [getProductTypeState])

  const previousYear = useRef()
  useEffect(() => {
    if (contractYear && previousYear.current !== contractYear && contractType) {
      handleSelectYear(contractYear)
      previousYear.current = contractYear
    }

  }, [
    contractYear, handleSelectYear, contractType
  ])

  useEffect(() => {
    if (fieldData.length) {
      let total_acres = 0
      let total_units = 0
      fieldData.forEach(field => {
        if(Number(field.planting_rate) !== 0){
          const acres = Number(field.acres)
          total_acres += acres
          total_units += acres * field.average_yield
        }
      })
      setTotalAcres(Math.round(total_acres))
      setTotalUnits(Math.round(total_units))
    }
  }, [
    fieldData, setTotalAcres, setTotalUnits
  ])

  useEffect(() => {
    let remaining_acres = totalAcres
    let remaining_units = totalUnits
    const allStates = getProductTypeState('ALL')
    allStates.forEach(({ state }) => {
      for (let row in state) {
        remaining_acres -= state[row].contract_acres
        remaining_units -= state[row].contract_units
      }
    })
    const temp = { remaining_acres, remaining_units }
    setRemaining(temp)
  }, [
    getProductTypeState, setRemaining, totalAcres, totalUnits
  ])

  useEffect(() => {
    if (initialMonthlyUnits?.length && newMonthlyUnits) {
      const allInputs = getProductTypeState('ALL')
      allInputs.forEach(({ type, monthsSetter }) => {
        const initialType = initialMonthlyUnits.filter(unit => {
          return unit.end_product_type === type || unit.delivery_type === type
        })
        if (initialType.length) {
          monthsSetter(prev => {
            const temp = [...prev]
            temp.forEach(monthInput => {
              initialType.forEach(unit => {
                if (monthInput.month === unit.month && monthInput.year === unit.year) {
                  monthInput.available_acres = unit.available_acres
                  monthInput.available_units = unit.available_units || 0
                }
              })
            })
            return temp
          })
        }
      })
      updatedMonthlyUnits(false)
    }
  }, [
    getProductTypeState, initialMonthlyUnits, newMonthlyUnits, updatedMonthlyUnits
  ])

  useEffect(() => {
    if(!checkboxStatus) {
      const returnArray = []
      const allStates = getProductTypeState('ALL')
      allStates.forEach(({ type, state }) => {
        for (let row in state) {
          if (state[row].contract_acres && state[row].contract_units) {
            const initMonth = initialMonthlyUnits?.length ?
              _.find(initialMonthlyUnits, monthObj => {
                return monthObj.month === state[row].month && monthObj.year === state[row].year
              })
              :
              null
            const newObj = {
              delivery_month: state[row].month,
              delivery_year: typeof state[row].year === 'string' ? parseInt(state[row].year) : state[row].year,
              delivery_type: type,
              contract_acres: typeof state[row].contract_acres === 'string' ? parseInt(state[row].contract_acres) : state[row].contract_acres,
              contract_units: state[row].contract_units,
              available_acres: initMonth ? parseInt(initMonth.available_acres) : 0
            }
            returnArray.push(newObj)
          }
        }
      })
      returnData({ type: 'UPDATE_DELIVERY_SCHEDULE', payload: returnArray.length ? returnArray : emptyArray })
      if (returnArray.length !== 0) {
        returnData({ type: 'CLEAR_ERROR', payload: { delivery_schedule: { empty: false, cattle_info: false } } })
      }
    }
  }, [
    getProductTypeState, initialMonthlyUnits, returnData, checkboxStatus
  ])

/* eslint-disable*/
  useEffect(() => {
    if (contractType === FED_ON_FARM) {
      if (selectedCattleType && headCount) {
        if (selectedCattleType.toLowerCase() === 'dairy') {
          setBushels(headCount * DAIRY_MULTIPLIER)
        } else if (selectedCattleType.toLowerCase() === 'beef') {
          setBushels(headCount * BEEF_MULTIPLIER)
        }
        returnData({ type: 'UPDATE_CATTLE_INFO', payload: { cattle_type: selectedCattleType.toLowerCase(), unit_count: Number(headCount) } })
        returnData({ type: 'CLEAR_ERROR', payload: { delivery_schedule: { empty: false, cattle_info: false } } })
      } else if (!selectedCattleType) {
        returnData({ type: 'UPDATE_CATTLE_INFO', payload: { cattle_type: null } })
        setBushels(0)
      } else if (!headCount) {
        returnData({ type: 'UPDATE_CATTLE_INFO', payload: { unit_count: 0 } })
        setBushels(0)
      }
    }
  }, [
    selectedCattleType, headCount, returnData, contractType
  ])

  useEffect(() => {
    const formatDeliverySchedule = () => {
      const deliveryTypes = [
        { deliveryType: 'grain', setter: setGrainInputs, monthsSetter: setGrainMonths },
        { deliveryType: 'silage', setter: setSilageInputs, monthsSetter: setSilageMonths },
        { deliveryType: 'earlage', setter: setEarlageInputs, monthsSetter: setEarlageMonths },
        { deliveryType: 'high-moisture', setter: setHighMoistureCornInputs, monthsSetter: setHighMoistureCornMonths }
      ]

      deliveryTypes.forEach(({ deliveryType, setter, monthsSetter }) => {
        const typesOfProdPlanUnits = initialMonthlyUnits.filter(unit => {
          return unit.end_product_type === deliveryType
        })
        if (typesOfProdPlanUnits?.length) {
          monthsSetter(prev => {
            const temp = [...prev]
            temp.forEach(monthInput => {
              typesOfProdPlanUnits.forEach(unit => {
                if (monthInput.month === unit.month && monthInput.year === unit.year) {
                  monthInput.available_acres = unit.available_acres
                  monthInput.available_units = unit.available_units || 0
                }
              })
            })
            return temp
          })
        }

        const filteredUnits = _defaultDeliverySchedule.filter(comUnit => {
          return comUnit.delivery_type === deliveryType
        })
        const inputObject = generateMonthObject(contractYear, deliveryType)
        if (filteredUnits.length) {
          filteredUnits.forEach(comUnit => {
            const delivery_month = moment(`${comUnit.delivery_month}, 1 ${comUnit.delivery_year}`).format('MMM-YY')
            const newUnit = {
              ...comUnit,
              month: comUnit.delivery_month,
              delivery_month,
              year: comUnit.delivery_year
            }
            inputObject[delivery_month] = newUnit

          })
        }
        setter(inputObject)
      })
    }
    if (_defaultDeliverySchedule?.length && initialMonthlyUnits?.length && contractType) {
      formatDeliverySchedule()
    }
  }, [
    _defaultDeliverySchedule, contractType, contractYear, initialMonthlyUnits, updatedMonthlyUnits
  ])

  useEffect(() => {
    if (_defaultCattleInfo) {
      setSelectedCattleType(_defaultCattleInfo[0].cattle_type)
      setHeadCount(_defaultCattleInfo[0].unit_count)
    }
  }, [_defaultCattleInfo])

  const unitLabel = type => {
    switch (type) {
      case 'grain':
        return 'bushel'
      case 'high moisture corn':
        return 'wet bushel'
      default:
        return 'tons'
    }
  }

  const tabGenerator = type => {
    const value = convertToSentenceCase(type)
    const upperType = type.toUpperCase()
    const { state, months } = getProductTypeState(upperType)
    let unit = unitLabel(type)
    return {
      value,
      data: months,
      structure: {
        headers: [
          'Delivery month', 'Available acres', 'Available bushel', 'Contract acres', `Contract ${unit}`
        ],
        dataKeys: [
          'delivery_month', 'available_acres', 'available_units', 'contract_acres', 'contract_units'
        ],
        rowKey: 'delivery_month',
        rowFormat: {
          delivery_month: {
            overwriteStyles: true,
            customStyle: 'df aic flx2'
          },
          available_acres: {
            format: available_acres => {
              return (<span>{available_acres}</span>)
            }
          },
          available_units: {
            format: available_units => {
              return (<span>{available_units}</span>)
            }
          },
          contract_acres: {
            overwriteStyles: true,
            customStyle: 'flx1 mr25',
            allContent: true,
            format: rowContent => {
              return (
                <input
                  type='number'
                  className=''
                  min = "0"
                  aria-label={`${rowContent.delivery_month}_contract_acres`}
                  value={state[rowContent.delivery_month]?.contract_acres}
                  onChange={e => updateDeliveryMonth(e, rowContent.delivery_month, 'contract_acres', upperType)} />
              )
            }
          },
          contract_units: {
            overwriteStyles: true,
            customStyle: 'flx1 mr25',
            allContent: true,
            format: rowContent => {
              return (
                <input
                  type='number'
                  className=''
                  min = "0"
                  aria-label={`${rowContent.delivery_month}_contract_units`}
                  value={state[rowContent.delivery_month]?.contract_units}
                  onChange={e => updateDeliveryMonth(e, rowContent.delivery_month, 'contract_units', upperType)} />
              )
            }
          }
        }
      }
    }
  }

  const generateTabs = () => {
    const grain = tabGenerator('grain')
    if (contractType === COMMERCIAL_FEED) {
      return [
        grain, tabGenerator('silage'), tabGenerator('earlage'), tabGenerator('high moisture corn')
      ]
    } else {
      return [grain]
    }
  
  }

  const tabs = generateTabs()

  useEffect(() => {
    if(remaining.remaining_units === 0) {
      returnData({ type: 'UPDATE_REMAINING_BUSHLES', payload: false })
    } else {
      returnData({ type: 'UPDATE_REMAINING_BUSHLES', payload: true })
    }
  },[remaining.remaining_units, returnData])

  const bottomRow = (
    <div className='bt1-s-lightgray df jcfe aic h44'>
      <div className='flx4'></div>
      <div className='flx1 df jcfs mr25'>{Math.round(remaining.remaining_acres)} remaining</div>
      <div className='flx1 df jcfs mr25'>{Math.round(remaining.remaining_units)} remaining</div>
    </div>
  )

  const fedOnFarmRender = () => {
    return (
      <form>
        <div className='df jcsb'>
          <div className='w45%'>
            <span>Cattle type</span>
            <div className='mt5'>
              <Select
                defaultId={selectedCattleType}
                placeholder='Select an option'
                handleSelection={option => setSelectedCattleType(option.value)}
                options={[{ id: 'beef', value: 'Beef' }, { id: 'dairy', value: 'Dairy' }]}
              />
            </div>
          </div>
          <div className='w45%'>
            <label htmlFor='cattleHeadCount'>Cattle Headcount</label>
            <input className='mt5' type='number' value={headCount} onChange={e => setHeadCount(e.target.value)} placeholder='Enter count' name='cattleHeadCount' id='cattleHeadCount' />
          </div>
        </div>
        <div className='mt25'>
          <span>Estimated Bushel</span>
          <div className='ml10 mt15 c-darkwhite'>{bushels}</div>
        </div>
      </form>
    )
  }


  return (
    <Widget title='Delivery' dataTestId='deliveryScheduleWidget'>
      {contractType ? (
        <div className='mt20 mb20'>
          {contractType === FED_ON_FARM ? (
            fedOnFarmRender()
          ) : (
            <TabbedTable
              IsInputTable={false}
              editMode={false}
              tabs={tabs}
              statusComponent={
                initialMonthlyUnits?.length ? null : (
                  <TableStatus status={'EMPTY'} prompt='Please select a contract type and year' />
                )
              }
              bottomOfTable={bottomRow}
            />
          )}
        </div>
      ) : (
        <TableStatus status={'LOADING'} />
      )}
    </Widget>
  )
}

export default DeliveryScheduleWidget
